import { useState } from "react";
import { toast } from "sonner";
import { Lock, KeyRound } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { cardCls, inputCls, btnPrimary } from "@/components/shared";

export const PinInput = ({ value, onChange, testId, autoFocus, ...rest }) => (
  <input data-testid={testId} type="password" inputMode="numeric" autoComplete="off" maxLength={4} autoFocus={autoFocus}
    value={value} onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="••••"
    className={`${inputCls} text-center text-2xl tracking-[0.8em] font-semibold max-w-[200px] mx-auto block`} {...rest} />
);

const ForgotPin = () => {
  const { setUser } = useAuth();
  const [password, setPassword] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      setUser((await api.post("/diary/pin/off", { password })).data.user);
      toast.success("PIN turned off. You can set a new one in diary settings.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <form onSubmit={submit} className="mt-6 pt-6 border-t border-t-line space-y-3 text-left" data-testid="diary-forgot-form">
      <p className="text-xs text-t-muted">Enter your account password to turn the diary PIN off.</p>
      <input data-testid="diary-forgot-password-input" type="password" className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Account password" required />
      <button type="submit" data-testid="diary-forgot-submit" className={`${btnPrimary} w-full`}>Turn off PIN</button>
    </form>
  );
};

export const PinLock = ({ onUnlock }) => {
  const { user } = useAuth();
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [forgot, setForgot] = useState(false);
  const submit = async (value) => {
    try {
      onUnlock((await api.post("/diary/unlock", { pin: value })).data.token);
    } catch (err) {
      setError(formatApiError(err));
      setPin("");
    }
  };
  const change = (v) => {
    setPin(v);
    setError("");
    if (v.length === 4) submit(v);
  };
  return (
    <div className="min-h-[60vh] flex items-center justify-center" data-testid="diary-lock">
      <div className={`${cardCls} p-8 sm:p-10 w-full max-w-sm text-center fade-up`}>
        <div className="h-14 w-14 rounded-2xl bg-t-primary-soft text-t-primary flex items-center justify-center mx-auto mb-5"><Lock className="h-6 w-6" /></div>
        <h1 className="font-serif-display text-3xl font-semibold">Your diary is locked</h1>
        <p className="text-sm text-t-muted mt-2 mb-6">Enter your 4-digit PIN to open it.</p>
        <PinInput testId="diary-unlock-pin-input" value={pin} onChange={change} autoFocus />
        {error && <p className="text-sm text-t-danger mt-3" data-testid="diary-unlock-error">{error}</p>}
        {user.has_password && !forgot && (
          <button type="button" data-testid="diary-forgot-pin" onClick={() => setForgot(true)} className="mt-6 text-xs text-t-primary hover:text-t-primary-hover inline-flex items-center gap-1">
            <KeyRound className="h-3 w-3" /> Forgot PIN?
          </button>
        )}
        {forgot && <ForgotPin />}
      </div>
    </div>
  );
};
